import assert from "assert";

import { enums, enumByIdentifier, items, stringifyReplacer, unexpectedCodePath, WHEN_WITHIN, enumWith, enumsWith } from "@/fx/data-helpers";

export namespace CLP {

// #region SchemaVersion

  export type SchemaVersion = {
    identifier: string;
    name:       string;
  }
  export const SchemaVersion = enums({
    V1: { identifier: "V1", name: "v1" } as SchemaVersion,
    V2: { identifier: "V2", name: "v2" } as SchemaVersion,
  })
  export const schemaVersions = items<SchemaVersion>(SchemaVersion);
  export const schemaVersion  = enumByIdentifier(schemaVersions);

// #endregion
// #region ActorType

  export type ActorType = {
    identifier: string;
    name:       string;
    shortName:  string;
  }
  export const ActorType = enums({
    RETURNS_COORDINATOR:                    { identifier: "RETURNS_COORDINATOR",                    name: "Returns Coordinator",                    shortName: "RC"      } as ActorType,
    DIVISION_SUPPORT_SPECIALIST:            { identifier: "DIVISION_SUPPORT_SPECIALIST",            name: "Division Support Specialist",            shortName: "DSS"     } as ActorType,
    CUSTOMER_OPERATIONS_SPECIALIST:         { identifier: "CUSTOMER_OPERATIONS_SPECIALIST",         name: "Customer Operations Specialist",         shortName: "COS"     } as ActorType,
    WHOLESALER_CREDIT_ACCOUNTANT:           { identifier: "WHOLESALER_CREDIT_ACCOUNTANT",           name: "Wholesaler Credit Accountant",           shortName: "WCA"     } as ActorType,
    WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT: { identifier: "WHOLESALER_CREDIT_ACCOUNTANT_ASSISTANT", name: "Wholesaler Credit Accountant Assistant", shortName: "WCA_AS"  } as ActorType,
    ACCOUNTS_RECEIVABLE_SUPERVISOR:         { identifier: "ACCOUNTS_RECEIVABLE_SUPERVISOR",         name: "Accounts Receivable Supervisor",         shortName: "ARS"     } as ActorType,
    SYSTEM:                                 { identifier: "SYSTEM",                                 name: "System",                                 shortName: "SYS"     } as ActorType,
    BBC_APPROVER:                           { identifier: "BBC_APPROVER",                           name: "BBC Approver",                           shortName: "BBC_APR" } as ActorType,
    DISTRIBUTOR:                            { identifier: "DISTRIBUTOR",                            name: "Distributor",                            shortName: "DIS"     } as ActorType,
  })
  export const actorTypes = items<ActorType>(ActorType);
  export const actorType  = enumByIdentifier(actorTypes);

// #endregion
// #region Distributor Status

  export type DistributorStatus = {
    identifier:       string;
    name:             string;
  }
  export const DistributorStatus = enums({
    DRAFT:      { identifier: "DRAFT",      name: "Draft"      } as DistributorStatus,
    SUBMITTED:  { identifier: "SUBMITTED",  name: "Submitted"  } as DistributorStatus,
    PROCESSING: { identifier: "PROCESSING", name: "Processing" } as DistributorStatus,
    CREDITED:   { identifier: "CREDITED",   name: "Credited"   } as DistributorStatus,
    DENIED:     { identifier: "DENIED",     name: "Denied"     } as DistributorStatus,
    CANCELED:   { identifier: "CANCELED",   name: "Canceled"   } as DistributorStatus,
  })
  export const distributorStatuses = items<DistributorStatus>(DistributorStatus);
  export const distributorStatus   = enumByIdentifier(distributorStatuses);

// #endregion
// #region Stage

  export type Stage = {
    identifier:         string;
    name:               string;
    distributorStatus?: DistributorStatus;
  }
  export const Stage = enums({
    DRAFT:     { identifier: "DRAFT",     name: "Draft"                                                      } as Stage,
    SUBMITTED: { identifier: "SUBMITTED", name: "Submitted", distributorStatus: DistributorStatus.SUBMITTED  } as Stage,
    OPEN:      { identifier: "OPEN",      name: "Open",      distributorStatus: DistributorStatus.PROCESSING } as Stage,
    CLOSED:    { identifier: "CLOSED",    name: "Closed",                                                    } as Stage,
  })
  export const stages = items<Stage>(Stage);
  export const stage  = enumByIdentifier(stages);

// #endregion
// #region Phase

  export type Phase = {
    identifier: string;
    name:       string;
    stage:      Stage;
  }
  export const Phase = enums({
    CLAIM_SUBMISSION:           { identifier: "CLAIM_SUBMISSION",           name: "Claim Submission",           stage: Stage.DRAFT     } as Phase,
    CLAIM_CREATION:             { identifier: "CLAIM_CREATION",             name: "Claim Creation",             stage: Stage.DRAFT     } as Phase,
    CLAIM_INTAKE:               { identifier: "CLAIM_INTAKE",               name: "Claim Intake",               stage: Stage.SUBMITTED } as Phase,
    SAP_ORDER_CREATION:         { identifier: "SAP_ORDER_CREATION",         name: "SAP Order Creation",         stage: Stage.OPEN      } as Phase,
    CLAIM_APPROVAL:             { identifier: "CLAIM_APPROVAL",             name: "Claim Approval",             stage: Stage.OPEN      } as Phase,
    TTB_APPROVAL:               { identifier: "TTB_APPROVAL",               name: "TTB Approval",               stage: Stage.OPEN      } as Phase,
    DESTRUCTION:                { identifier: "DESTRUCTION",                name: "Destruction",                stage: Stage.OPEN      } as Phase,
    WHOLESALER_CREDIT_APPROVAL: { identifier: "WHOLESALER_CREDIT_APPROVAL", name: "Wholesaler Credit Approval", stage: Stage.OPEN      } as Phase,
    ARCHIVED:                   { identifier: "ARCHIVED",                   name: "Archived",                   stage: Stage.CLOSED    } as Phase,
  })
  export const phases = items<Phase>(Phase);
  export const phase  = enumByIdentifier(phases);

// #endregion
// #region Deliverable

  export type Deliverable = {
    identifier:       string;
    name:             string;
    phase:            Phase;
  }
  export const Deliverable = enums({
    CLAIM_SUBMISSION:           { identifier: "CLAIM_SUBMISSION",           name: "Claim Submission",           phase: Phase.CLAIM_SUBMISSION           } as Deliverable,
    CLAIM_CREATION:             { identifier: "CLAIM_CREATION",             name: "Claim Creation",             phase: Phase.CLAIM_CREATION             } as Deliverable,
    FIRST_LEVEL_CLAIM_APPROVAL: { identifier: "FIRST_LEVEL_CLAIM_APPROVAL", name: "First Level Claim Approval", phase: Phase.CLAIM_INTAKE               } as Deliverable,
    CLAIM_ACCEPTANCE:           { identifier: "CLAIM_ACCEPTANCE",           name: "Claim Acceptance",           phase: Phase.CLAIM_INTAKE               } as Deliverable,
    SAP_ORDER:                  { identifier: "SAP_ORDER",                  name: "SAP Order",                  phase: Phase.SAP_ORDER_CREATION         } as Deliverable,
    CLAIM_APPROVAL_REQUESTS:    { identifier: "CLAIM_APPROVAL_REQUESTS",    name: "Claim Approval Requests",    phase: Phase.CLAIM_APPROVAL             } as Deliverable,
    CLAIM_APPROVALS:            { identifier: "CLAIM_APPROVALS",            name: "Claim Approvals",            phase: Phase.CLAIM_APPROVAL             } as Deliverable,
    TTB_SUBMISSION:             { identifier: "TTB_SUBMISSION",             name: "TTB Submission",             phase: Phase.TTB_APPROVAL               } as Deliverable,
    TTB_APPROVAL:               { identifier: "TTB_APPROVAL",               name: "TTB Approval",               phase: Phase.TTB_APPROVAL               } as Deliverable,
    APPROVAL_TO_DESTROY:        { identifier: "APPROVAL_TO_DESTROY",        name: "Approval to Destroy",        phase: Phase.DESTRUCTION                } as Deliverable,
    DESTRUCTION:                { identifier: "DESTRUCTION",                name: "Destruction",                phase: Phase.DESTRUCTION                } as Deliverable,
    WHOLESALER_CREDIT_APPROVAL: { identifier: "WHOLESALER_CREDIT_APPROVAL", name: "Wholesaler Credit Approval", phase: Phase.WHOLESALER_CREDIT_APPROVAL } as Deliverable,
  })
  export const deliverables = items<Deliverable>(Deliverable);
  export const deliverable  = enumByIdentifier(deliverables);

// #endregion
// #region Action

  export const timeExpectations = {
    CLAIM_APPROVALS:  2,
    TTB_FOLLOW_UP:    7,
    TTB_APPROVAL:    14,
  }

  export type PrimaryAction = {
    type:         "primary";
    identifier:   string;
    name:         string;
    assignedTo:   ActorType;
    deliverable:  Deliverable;
  }
  export type DelegatedAction = {
    type:               "delegated";
    identifier:         string;
    name:               string;
    delegatedTo:        string;
    deliverable:        Deliverable;
    supportedBy:        SupportingAction[];
    ghostedBy:          GhostAction;
    overdueCorrectedBy: OverdueCorrectionAction;
  }
  export type SupportingAction = {
    type:       "supporting";
    identifier: string;
    name:       string;
    assignedTo: ActorType;
    for:        DelegatedAction;
    assignable: ({ dueDate, now }: { dueDate: Date, now: Date }) => boolean;
  }
  export type GhostAction = {
    type:       "ghost";
    identifier: string;
    name:       string;
    assignedTo: ActorType;
    for:        DelegatedAction;
  }
  export type OverdueCorrectionAction = {
    type:       "overdue-correction";
    identifier: string;
    name:       string;
    assignedTo: ActorType;
    for:        DelegatedAction;
  }
  export type BaselineAction   = PrimaryAction | DelegatedAction;
  export type AssignableAction = PrimaryAction                   | SupportingAction               | OverdueCorrectionAction;
  export type Action           = PrimaryAction | DelegatedAction | SupportingAction | GhostAction | OverdueCorrectionAction;

  export function isPrimaryAction(action: Action): action is PrimaryAction {
    assert(action !== undefined)
    return action.type === "primary";
  }
  export function isDelegatedAction(action: Action): action is DelegatedAction {
    assert(action !== undefined)
    return action.type === "delegated";
  }
  export function isSupportingAction(action: Action): action is SupportingAction {
    assert(action !== undefined)
    return action.type === "supporting";
  }
  export function isGhostAction(action: Action): action is GhostAction {
    assert(action !== undefined)
    return action.type === "ghost";
  }
  export function isOverdueCorrectionAction(action: Action): action is OverdueCorrectionAction {
    assert(action !== undefined)
    return action.type === "overdue-correction";
  }
  export function isBaselineAction(action: Action): action is BaselineAction {
    assert(action !== undefined)
    return isPrimaryAction(action) || isDelegatedAction(action);
  }
  export function isAssignableAction(action: Action): action is AssignableAction {
    assert(action !== undefined)
    return isPrimaryAction(action) || isSupportingAction(action) || isOverdueCorrectionAction(action);
  }
  export function action_assignedTo(action: Action): ActorType | undefined {
    assert(action !== undefined)
    return isPrimaryAction          (action) ? action.assignedTo
         : isSupportingAction       (action) ? action.assignedTo
         : isGhostAction            (action) ? action.assignedTo
         : isOverdueCorrectionAction(action) ? action.assignedTo
                                             : undefined;
  }
  export function action_deliverable(action: Action): Deliverable {
    assert(action !== undefined)
    return isPrimaryAction          (action) ? action.deliverable
         : isDelegatedAction        (action) ? action.deliverable
         : isSupportingAction       (action) ? action.for.deliverable
         : isGhostAction            (action) ? action.for.deliverable
         : isOverdueCorrectionAction(action) ? action.for.deliverable
                                             : unexpectedCodePath();
  }

  export function actions_forDeliverables({ deliverables, overdue }: {
    deliverables: Deliverable[],
    overdue: boolean
  }) {
    if (overdue) {
      return actions.filter(_ =>
        (isGhostAction            (_) && deliverables.includes(_.for.deliverable)) ||
        (isOverdueCorrectionAction(_) && deliverables.includes(_.for.deliverable))
      );
    } else {
      return actions.filter(_ =>
        (isBaselineAction         (_) && deliverables.includes(_.deliverable    )) ||
        (isGhostAction            (_) && deliverables.includes(_.for.deliverable))
      );
    }
  }

  export function actionMatch<TOutput>({ action }: { action: Action }) {
    return ({ primary, delegated, supporting, ghost, overdueCorrection }: {
      primary:           (action: PrimaryAction          ) => TOutput,
      delegated:         (action: DelegatedAction        ) => TOutput,
      supporting:        (action: SupportingAction       ) => TOutput,
      ghost:             (action: GhostAction            ) => TOutput,
      overdueCorrection: (action: OverdueCorrectionAction) => TOutput
    }) => {
      switch (action.type) {
        case "primary":            return primary          (action);
        case "delegated":          return delegated        (action);
        case "supporting":         return supporting       (action);
        case "ghost":              return ghost            (action);
        case "overdue-correction": return overdueCorrection(action);

        default: throw new Error(`Unknown action type: ${(action as Action).type}`);
      }
    };
  }

  export const Action = enums({
    SELECT_PRODUCTS:                   { type: "primary",            identifier: "SELECT_PRODUCTS",                   name: "Select Products",                   assignedTo: ActorType.DISTRIBUTOR,                  deliverable: Deliverable.CLAIM_SUBMISSION               } as PrimaryAction,
    ENTER_PRODUCT_DETAILS:             { type: "primary",            identifier: "ENTER_PRODUCT_DETAILS",             name: "Enter Product Details",             assignedTo: ActorType.DISTRIBUTOR,                  deliverable: Deliverable.CLAIM_SUBMISSION               } as PrimaryAction,
    FINISH_PRODUCT_DETAILS:            { type: "primary",            identifier: "FINISH_PRODUCT_DETAILS",            name: "Finish Product Details",                                                                                                                        } as PrimaryAction,
    SPECIFY_DESTRUCTION_INTENT:        { type: "primary",            identifier: "SPECIFY_DESTRUCTION_INTENT",        name: "Specify Destruction Intent",        assignedTo: ActorType.DISTRIBUTOR,                  deliverable: Deliverable.CLAIM_SUBMISSION               } as PrimaryAction,
    VERIFY_POINT_OF_CONTACT:           { type: "primary",            identifier: "VERIFY_POINT_OF_CONTACT",           name: "Verify Point of Contact",           assignedTo: ActorType.DISTRIBUTOR,                  deliverable: Deliverable.CLAIM_SUBMISSION               } as PrimaryAction,
    CONFIRM_AND_SUBMIT:                { type: "primary",            identifier: "CONFIRM_AND_SUBMIT",                name: "Confirm and Submit",                assignedTo: ActorType.DISTRIBUTOR,                  deliverable: Deliverable.CLAIM_SUBMISSION               } as PrimaryAction,

    GET_CLAIM_APPROVAL:                { type: "primary",            identifier: "GET_CLAIM_APPROVAL",                name: "Get Claim Approval",                                                                                                                            } as PrimaryAction,

    APPROVE_CLAIM:                     { type: "primary",            identifier: "APPROVE_CLAIM",                     name: "Approve Claim",                     assignedTo: ActorType.BBC_APPROVER,                 deliverable: Deliverable.FIRST_LEVEL_CLAIM_APPROVAL     } as PrimaryAction,

    CONFIRM_CLAIM_ACCEPTANCE:          { type: "primary",            identifier: "CONFIRM_CLAIM_ACCEPTANCE",          name: "Confirm Claim Acceptance",          assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.CLAIM_ACCEPTANCE               } as PrimaryAction,
    RECORD_SAP_ORDER_NUMBER:           { type: "primary",            identifier: "RECORD_SAP_ORDER_NUMBER",           name: "Record SAP Order Number",           assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.SAP_ORDER                      } as PrimaryAction,
    CONFIRM_SAP_ORDER_CREATED:         { type: "primary",            identifier: "CONFIRM_SAP_ORDER_CREATED",         name: "Confirm SAP Order Created",         assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.SAP_ORDER                      } as PrimaryAction,
    ENTER_WHOLESALER_POINT_OF_CONTACT: { type: "primary",            identifier: "ENTER_WHOLESALER_POINT_OF_CONTACT", name: "Enter Wholesaler Point of Contact", assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.CLAIM_CREATION                 } as PrimaryAction,
    ENTER_CLAIM_APPROVAL_DETAILS:      { type: "primary",            identifier: "ENTER_CLAIM_APPROVAL_DETAILS",      name: "Enter Claim Approval Details",      assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.CLAIM_CREATION                 } as PrimaryAction,
    UPLOAD_CLAIM_FORM:                 { type: "primary",            identifier: "UPLOAD_CLAIM_FORM",                 name: "Upload Claim Form",                 assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.CLAIM_CREATION                 } as PrimaryAction,
    REVIEW_AND_OPEN_CLAIM:             { type: "primary",            identifier: "REVIEW_AND_OPEN_CLAIM",             name: "Review and Open Claim",             assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.CLAIM_CREATION                 } as PrimaryAction,

    SEND_CLAIM_APPROVAL_REQUESTS:      { type: "primary",            identifier: "SEND_CLAIM_APPROVAL_REQUESTS",      name: "Send Claim Approval Requests",      assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.CLAIM_APPROVAL_REQUESTS        } as PrimaryAction,
    WAITING_FOR_CLAIM_APPROVALS:       { type: "delegated",          identifier: "WAITING_FOR_CLAIM_APPROVALS",       name: "Waiting for Claim Approvals",       delegatedTo: "bbcApprover",                             deliverable: Deliverable.CLAIM_APPROVALS                } as DelegatedAction,
    RECORD_CLAIM_APPROVALS:            { type: "ghost",              identifier: "RECORD_CLAIM_APPROVALS",            name: "Record Claim Approvals",            assignedTo: ActorType.RETURNS_COORDINATOR                                                                   } as GhostAction,
    FOLLOW_UP_ON_CLAIM_APPROVALS:      { type: "overdue-correction", identifier: "FOLLOW_UP_ON_CLAIM_APPROVALS",      name: "Follow Up on Claim Approvals",      assignedTo: ActorType.RETURNS_COORDINATOR                                                                   } as OverdueCorrectionAction,
    CONFIRM_CLAIM_APPROVALS:           { type: "primary",            identifier: "CONFIRM_CLAIM_APPROVALS",           name: "Confirm Claim Approvals",           assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.CLAIM_APPROVALS                } as PrimaryAction,

    PREPARE_TTB_SUBMISSION:            { type: "primary",            identifier: "PREPARE_TTB_SUBMISSION",            name: "Prepare TTB Documents",             assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.TTB_SUBMISSION                 } as PrimaryAction,
    SEND_TTB_SUBMISSION:               { type: "primary",            identifier: "SEND_TTB_SUBMISSION",               name: "Submit TTB Documents",              assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.TTB_SUBMISSION                 } as PrimaryAction,
    WAITING_FOR_TTB_APPROVAL:          { type: "delegated",          identifier: "WAITING_FOR_TTB_APPROVAL",          name: "Waiting for TTB Approval",          delegatedTo: "ttb",                                 deliverable: Deliverable.TTB_APPROVAL                   } as DelegatedAction,
    RECORD_TTB_APPROVAL:               { type: "ghost",              identifier: "RECORD_TTB_APPROVAL",               name: "Record TTB Approval",               assignedTo: ActorType.RETURNS_COORDINATOR                                                                   } as GhostAction,
    FOLLOW_UP_WITH_TTB:                { type: "supporting",         identifier: "FOLLOW_UP_WITH_TTB",                name: "Follow Up with TTB",                assignedTo: ActorType.RETURNS_COORDINATOR,          assignable: WHEN_WITHIN(timeExpectations.TTB_FOLLOW_UP) } as SupportingAction,
    REGISTER_TTB_APPROVAL_OVERDUE:     { type: "overdue-correction", identifier: "REGISTER_TTB_APPROVAL_OVERDUE",     name: "Confirm Overdue TTB Approval",      assignedTo: ActorType.RETURNS_COORDINATOR                                                                   } as OverdueCorrectionAction,
    CONFIRM_TTB_APPROVAL:              { type: "primary",            identifier: "CONFIRM_TTB_APPROVAL",              name: "Confirm TTB Approval",              assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.TTB_APPROVAL                   } as PrimaryAction,

    SEND_APPROVAL_TO_DESTROY:          { type: "primary",            identifier: "SEND_APPROVAL_TO_DESTROY",          name: "Send Approval to Destroy",          assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.APPROVAL_TO_DESTROY            } as PrimaryAction,
    CONFIRM_APPROVAL_TO_DESTROY:       { type: "primary",            identifier: "CONFIRM_APPROVAL_TO_DESTROY",       name: "Confirm Approval to Destroy",       assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.APPROVAL_TO_DESTROY            } as PrimaryAction,
    WAITING_FOR_AODS:                  { type: "delegated",          identifier: "WAITING_FOR_AODS",                  name: "Waiting for AODs",                  delegatedTo: "wholesaler",                          deliverable: Deliverable.DESTRUCTION                    } as DelegatedAction,
    RECORD_AODS:                       { type: "ghost",              identifier: "RECORD_AODS",                       name: "Record AODs",                       assignedTo: ActorType.RETURNS_COORDINATOR                                                                   } as GhostAction,
    FOLLOW_UP_ON_AODS:                 { type: "overdue-correction", identifier: "FOLLOW_UP_ON_AODS",                 name: "Follow Up on AODs",                 assignedTo: ActorType.RETURNS_COORDINATOR                                                                   } as OverdueCorrectionAction,
    CONFIRM_DESTRUCTION:               { type: "primary",            identifier: "CONFIRM_DESTRUCTION",               name: "Confirm Destruction",               assignedTo: ActorType.RETURNS_COORDINATOR,          deliverable: Deliverable.DESTRUCTION                    } as PrimaryAction,

    CONFIRM_CLAIM_ENTERED_IN_SAP:      { type: "primary",            identifier: "CONFIRM_CLAIM_ENTERED_IN_SAP",      name: "Confirm Claim Entered in SAP",      assignedTo: ActorType.WHOLESALER_CREDIT_ACCOUNTANT, deliverable: Deliverable.WHOLESALER_CREDIT_APPROVAL     } as PrimaryAction,
    RECORD_CREDIT_DETAILS:             { type: "primary",            identifier: "RECORD_CREDIT_DETAILS",             name: "Record Credit Details",             assignedTo: ActorType.WHOLESALER_CREDIT_ACCOUNTANT, deliverable: Deliverable.WHOLESALER_CREDIT_APPROVAL     } as PrimaryAction,
    CONFIRM_WHOLESALER_CREDITED:       { type: "primary",            identifier: "CONFIRM_WHOLESALER_CREDITED",       name: "Confirm Wholesaler Credited",       assignedTo: ActorType.WHOLESALER_CREDIT_ACCOUNTANT, deliverable: Deliverable.WHOLESALER_CREDIT_APPROVAL     } as PrimaryAction,

    CONFIRM_ISSUE_RESOLVED:            { type: "primary",            identifier: "CONFIRM_ISSUE_RESOLVED",            name: "Confirm Issue Resolved",                                                                                                                        } as PrimaryAction,
  })
  export const actions      = items<Action>(Action);
  export const action       = enumByIdentifier(actions);
  export const action_WITH  = enumWith(actions);
  export const actions_WITH = enumsWith(actions);

  connectActions({
    delegated:         Action.WAITING_FOR_CLAIM_APPROVALS,
    ghost:             Action.RECORD_CLAIM_APPROVALS,
    overdueCorrection: Action.FOLLOW_UP_ON_CLAIM_APPROVALS,
  });
  connectActions({
    delegated:         Action.WAITING_FOR_TTB_APPROVAL,
    supporting:        [Action.FOLLOW_UP_WITH_TTB],
    ghost:             Action.RECORD_TTB_APPROVAL,
    overdueCorrection: Action.REGISTER_TTB_APPROVAL_OVERDUE,
  });
  connectActions({
    delegated:         Action.WAITING_FOR_AODS,
    ghost:             Action.RECORD_AODS,
    overdueCorrection: Action.FOLLOW_UP_ON_AODS,
  });

  function connectActions({ delegated, supporting, ghost, overdueCorrection }: {
    delegated:         DelegatedAction,
    supporting?:       SupportingAction[],
    ghost:             GhostAction,
    overdueCorrection: OverdueCorrectionAction
  }) {
    delegated.supportedBy        = supporting || [];
    delegated.ghostedBy          = ghost;
    delegated.overdueCorrectedBy = overdueCorrection;

    supporting?.forEach(_ => _.for = delegated);
    ghost            .for = delegated;
    overdueCorrection.for = delegated;
  }

// #endregion
// #region Bbc Status

    export type BbcStatus = {
      identifier:       string;
      name:             string;
      deliverables:     Deliverable[];
      overdue:          boolean;
      availableActions: Action[];
    }
    export const BbcStatus = enums({
      DRAFT:                              { identifier: "DRAFT",                              name: "Draft",                              overdue: false } as BbcStatus,
      SUBMITTED:                          { identifier: "SUBMITTED",                          name: "Submitted",                          overdue: false } as BbcStatus,
      PENDING_NEW_SAP_ORDER:              { identifier: "PENDING_NEW_SAP_ORDER",              name: "Pending New SAP Order",              overdue: false } as BbcStatus,
      PENDING_CLAIM_APPROVAL:             { identifier: "PENDING_CLAIM_APPROVAL",             name: "Pending Claim Approval",             overdue: false } as BbcStatus,
      CLAIM_APPROVAL_OVERDUE:             { identifier: "CLAIM_APPROVAL_OVERDUE",             name: "Claim Approval Overdue",             overdue: true  } as BbcStatus,
      PENDING_TTB_APPROVAL:               { identifier: "PENDING_TTB_APPROVAL",               name: "Pending TTB Approval",               overdue: false } as BbcStatus,
      TTB_APPROVAL_OVERDUE:               { identifier: "TTB_APPROVAL_OVERDUE",               name: "TTB Approval Overdue",               overdue: true  } as BbcStatus,
      PENDING_APPROVAL_TO_DESTROY:        { identifier: "PENDING_APPROVAL_TO_DESTROY",        name: "Pending Approval to Destroy",        overdue: false } as BbcStatus,
      PENDING_DESTRUCTION:                { identifier: "PENDING_DESTRUCTION",                name: "Pending Destruction",                overdue: false } as BbcStatus,
      DESTRUCTION_OVERDUE:                { identifier: "DESTRUCTION_OVERDUE",                name: "Destruction Overdue",                overdue: true  } as BbcStatus,
      PENDING_WHOLESALER_CREDIT:          { identifier: "PENDING_WHOLESALER_CREDIT",          name: "Pending Wholesaler Credit",          overdue: false } as BbcStatus,
      APPROVED_FOR_WHOLESALER_CREDIT:     { identifier: "APPROVED_FOR_WHOLESALER_CREDIT",     name: "Approved for Wholesaler Credit",     overdue: false } as BbcStatus,
      WHOLESALER_CREDITED:                { identifier: "WHOLESALER_CREDITED",                name: "Wholesaler Credited",                overdue: false } as BbcStatus,
      CANCELED:                           { identifier: "CANCELED",                           name: "Canceled",                           overdue: false } as BbcStatus,
      DENIED:                             { identifier: "DENIED",                             name: "Denied",                             overdue: false } as BbcStatus,
    })
    export const bbcStatuses = items<BbcStatus>(BbcStatus);
    export const bbcStatus  = enumByIdentifier(bbcStatuses);

    export function bbcStatus_FROM_name(name: string): BbcStatus {
      const status = bbcStatuses.find(_ => _.name.toLowerCase() === name.toLowerCase());
      if (status === undefined) {
        throw new Error(`Unknown BBC status name: ${name}`);
      }
      return status;
    }

    connectStatus({ whileIn: BbcStatus.DRAFT })({
      willDeliver: [
        Deliverable.CLAIM_SUBMISSION,
        Deliverable.CLAIM_CREATION,
      ],
      canExecute: [
        Action.SELECT_PRODUCTS,
        Action.ENTER_PRODUCT_DETAILS,
        Action.SPECIFY_DESTRUCTION_INTENT,
        Action.VERIFY_POINT_OF_CONTACT,
        Action.CONFIRM_AND_SUBMIT,
        Action.ENTER_WHOLESALER_POINT_OF_CONTACT,
        Action.ENTER_CLAIM_APPROVAL_DETAILS,
        Action.UPLOAD_CLAIM_FORM,
        Action.REVIEW_AND_OPEN_CLAIM,
        Action.SELECT_PRODUCTS,
        Action.ENTER_PRODUCT_DETAILS,
        Action.SPECIFY_DESTRUCTION_INTENT,
        Action.VERIFY_POINT_OF_CONTACT,
        Action.CONFIRM_AND_SUBMIT,
      ],
    });
    connectStatus({ whileIn: BbcStatus.SUBMITTED })({
      willDeliver: [
        Deliverable.FIRST_LEVEL_CLAIM_APPROVAL,
        Deliverable.CLAIM_ACCEPTANCE,
        Deliverable.SAP_ORDER,
      ],
      canExecute:  [
        Action.APPROVE_CLAIM,
        Action.CONFIRM_CLAIM_ACCEPTANCE,
        Action.RECORD_SAP_ORDER_NUMBER,
        Action.CONFIRM_SAP_ORDER_CREATED,
      ],
    });
    connectStatus({ whileIn: BbcStatus.PENDING_CLAIM_APPROVAL })({
      willDeliver: [
        Deliverable.CLAIM_APPROVAL_REQUESTS,
        Deliverable.CLAIM_APPROVALS,
      ],
      canExecute: [
        Action.SEND_CLAIM_APPROVAL_REQUESTS,
        Action.WAITING_FOR_CLAIM_APPROVALS,
        Action.RECORD_CLAIM_APPROVALS,
        Action.CONFIRM_CLAIM_APPROVALS,
      ],
    });
    connectStatus({ whileIn: BbcStatus.PENDING_NEW_SAP_ORDER })({
      willDeliver: [
        Deliverable.SAP_ORDER,
      ],
      canExecute: [
        Action.RECORD_SAP_ORDER_NUMBER,
        Action.CONFIRM_SAP_ORDER_CREATED,
      ],
    });
    connectStatus({ whileIn: BbcStatus.CLAIM_APPROVAL_OVERDUE })({
      willDeliver: [
        Deliverable.CLAIM_APPROVALS,
      ],
      canExecute: [
        Action.RECORD_CLAIM_APPROVALS,
        Action.FOLLOW_UP_ON_CLAIM_APPROVALS,
      ],
    });
    connectStatus({ whileIn: BbcStatus.PENDING_TTB_APPROVAL })({
      willDeliver: [
        Deliverable.TTB_SUBMISSION,
        Deliverable.TTB_APPROVAL,
      ],
      canExecute: [
        Action.PREPARE_TTB_SUBMISSION,
        Action.SEND_TTB_SUBMISSION,
        Action.WAITING_FOR_TTB_APPROVAL,
        Action.RECORD_TTB_APPROVAL,
        Action.CONFIRM_TTB_APPROVAL,
      ],
    });
    connectStatus({ whileIn: BbcStatus.TTB_APPROVAL_OVERDUE })({
      willDeliver: [
        Deliverable.TTB_APPROVAL,
      ],
      canExecute: [
        Action.RECORD_TTB_APPROVAL,
        Action.REGISTER_TTB_APPROVAL_OVERDUE,
      ],
    });
    connectStatus({ whileIn: BbcStatus.PENDING_APPROVAL_TO_DESTROY })({
      willDeliver: [
        Deliverable.APPROVAL_TO_DESTROY,
      ],
      canExecute: [
        Action.SEND_APPROVAL_TO_DESTROY,
        Action.CONFIRM_APPROVAL_TO_DESTROY,
      ],
    });
    connectStatus({ whileIn: BbcStatus.PENDING_DESTRUCTION })({
      willDeliver: [
        Deliverable.DESTRUCTION,
      ],
      canExecute: [
        Action.WAITING_FOR_AODS,
        Action.RECORD_AODS,
        Action.CONFIRM_DESTRUCTION,
      ],
    });
    connectStatus({ whileIn: BbcStatus.DESTRUCTION_OVERDUE })({
      willDeliver: [
        Deliverable.DESTRUCTION,
      ],
      canExecute: [
        Action.RECORD_AODS,
        Action.FOLLOW_UP_ON_AODS,
      ],
    });
    connectStatus({ whileIn: BbcStatus.PENDING_WHOLESALER_CREDIT })({
      willDeliver: [
        Deliverable.WHOLESALER_CREDIT_APPROVAL,
      ],
      canExecute: [
        Action.CONFIRM_CLAIM_ENTERED_IN_SAP,
        Action.RECORD_CREDIT_DETAILS,
        Action.CONFIRM_WHOLESALER_CREDITED,
      ],
    });
    connectStatus({ whileIn: BbcStatus.APPROVED_FOR_WHOLESALER_CREDIT })({
      willDeliver: [],
      canExecute: [],
    });
    connectStatus({ whileIn: BbcStatus.WHOLESALER_CREDITED })({
      willDeliver: [],
      canExecute: [],
    });
    connectStatus({ whileIn: BbcStatus.CANCELED })({
      willDeliver: [],
      canExecute: [],
    });
    connectStatus({ whileIn: BbcStatus.DENIED })({
      willDeliver: [],
      canExecute: [],
    });

    function connectStatus({ whileIn: action }: {
      whileIn: BbcStatus
    }) {
      assert(action.deliverables     === undefined, `Status ${action.identifier} already has deliverables assigned.`);
      assert(action.availableActions === undefined, `Status ${action.identifier} already has actions assigned.`     );

      return ({ willDeliver: deliverables, canExecute: actions }: {
        willDeliver: Deliverable[],
        canExecute:  Action[]
      }) => {
        action.deliverables     = deliverables;
        action.availableActions = actions;
      }
    }

    const statusErrors = bbcStatuses
      .map(_ => {
        const actual   = _.availableActions;
        const expected = actions_forDeliverables({ deliverables: _.deliverables, overdue: _.overdue });

        const diff = {
          extra:   actual  .filter(_ => !expected.includes(_)),
          missing: expected.filter(_ => !actual  .includes(_)),
        };

        return diff.extra.length === 0 && diff.missing.length === 0 ? undefined : { status: _, diff };
      })
      .filter(_ => _);

    assert(
      statusErrors.length === 0,
      `Inconsistencies found in assigned Actions to the following Statuses. ${JSON.stringify(statusErrors, stringifyReplacer, 2)}`
    );

// #endregion
}
